import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilesImagesUploadAreaAndTable from 'components/shared/files-images-upload-area-and-table/files-images-upload-area-and-table.component';
import { MillingJob, MillingJobType, MillingMaterial } from 'models/milling';
import { Restoration } from 'models/order';
import OrderShadeInfoPanel from './order-shade-info-panel.component';
import { vitaColors } from 'models/vita-colors-data';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';

type AddMillingJobDialogProps = {
    millingJobTypes: MillingJobType[];
    prePopulateFilename: string;
    restorations: Restoration[];
    open: boolean;
    onAdd: (job: Partial<MillingJob>) => void;
    onClose?: () => void;
};
const AddMillingJobDialog: FC<AddMillingJobDialogProps> = ({
    millingJobTypes,
    prePopulateFilename,
    restorations,
    open,
    onAdd,
    onClose,
}: AddMillingJobDialogProps) => {
    const { t: translate } = useTranslation(['milling-jobs', 'construction-props', 'orders', 'restoration-types']);
    const [filename, setFilename] = useState('');
    const [jobType, setJobType] = useState('');
    const [material, setMaterial] = useState('');
    const [color, setColor] = useState('');
    const [availableMaterials, setAvailableMaterials] = useState<string[]>([]);
    const [availableColors, setAvailableColors] = useState<string[]>([]);
    const [files, setFiles] = useState<File[]>([]);
    const [deadline, setDeadline] = useState<Date | null>(null);
    const [comment, setComment] = useState('');

    useEffect(() => {
        setFilename(prePopulateFilename);
    }, [prePopulateFilename]);

    const handleJobTypeChanged = (jobTypeName: string) => {
        const selectedJobType = millingJobTypes.find((jobType) => jobType.name === jobTypeName);
        if (selectedJobType) {
            setJobType(jobTypeName);
            setMaterial('');
            setColor('');
            setAvailableColors([]);
            setAvailableMaterials(selectedJobType.materials.map((material) => (material as MillingMaterial)?.name));
        }
    };

    const handleMaterialChanged = (materialName: string) => {
        const selectedJobType = millingJobTypes.find((findJobType) => findJobType.name === jobType);
        if (selectedJobType) {
            const selectedMaterial = (selectedJobType.materials as MillingMaterial[]).find(
                (material) => material.name === materialName,
            );
            setMaterial(materialName);
            setColor('');

            let colors = [] as string[];
            colors = selectedMaterial?.useVitaColors ? colors.concat(vitaColors) : colors;
            setAvailableColors(colors.concat(selectedMaterial?.additionalColors || []));
        }
    };

    const resetForm = () => {
        setJobType('');
        setMaterial('');
        setColor('');
        setFilename(prePopulateFilename);
        setAvailableMaterials([]);
        setAvailableColors([]);
        setFiles([]);
        setDeadline(null);
        setComment('');
    };

    const isValid = () => {
        const baseValidation =
            jobType &&
            material &&
            filename &&
            files &&
            jobType !== '' &&
            material !== '' &&
            filename !== '' &&
            files.length > 0;
        if (availableColors.length === 0) {
            return baseValidation;
        }
        return baseValidation && color && color !== '';
    };

    const handleAddJobClick = () => {
        if (isValid()) {
            onAdd({
                jobType: jobType,
                material: material,
                color: color,
                // Strip invalid filename characters
                filename: filename.replace(/[/\\?%*:|"<>]/g, '-') + '.zip',
                files: files,
                deadline: deadline,
                comment: comment,
            });
            resetForm();
        }
    };

    const handleChange = async (currentFiles: File[]) => {
        if (!currentFiles || currentFiles.length === 0) {
            return;
        }
        setFiles([...files, ...currentFiles]);
    };

    const handleClose = () => {
        if (onClose) {
            resetForm();
            onClose();
        }
    };

    const removeFile = (index: number) => {
        const currentFiles = [...files];
        currentFiles.splice(index, 1);
        setFiles(currentFiles);
    };

    const handleDateChange = (date: Date | null) => {
        setDeadline(date as Date);
    };

    const handleCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
        setComment(event.target.value);
    };

    return (
        <Fragment>
            <Dialog open={open} fullWidth maxWidth="lg">
                <DialogTitle>{translate('milling-jobs:ADD_MILLING_JOB')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label={translate('common:FILENAME')}
                                        required
                                        InputProps={{
                                            endAdornment: <InputAdornment position="start">.zip</InputAdornment>,
                                        }}
                                        value={filename}
                                        onChange={(event) => {
                                            setFilename(event.target.value);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required>
                                        <InputLabel id="type-select-label">
                                            {translate('milling-jobs:TYPE_OF_JOB')}
                                        </InputLabel>
                                        {millingJobTypes ? (
                                            <Select
                                                labelId="type-select-label"
                                                label={translate('milling-jobs:TYPE_OF_JOB')}
                                                value={jobType}
                                                onChange={(e) => handleJobTypeChanged(e.target.value)}
                                            >
                                                {millingJobTypes.map((jobType) => (
                                                    <MenuItem key={jobType._id} value={jobType.name}>
                                                        {jobType.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        ) : undefined}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth required>
                                        <InputLabel id="material-select-label">
                                            {translate('construction-props:MATERIAL')}
                                        </InputLabel>
                                        <Select
                                            labelId="material-select-label"
                                            label={translate('construction-props:MATERIAL')}
                                            value={material}
                                            onChange={(e) => handleMaterialChanged(e.target.value)}
                                            disabled={availableMaterials.length === 0}
                                        >
                                            {availableMaterials.map((material) => (
                                                <MenuItem key={material} value={material}>
                                                    {material}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {availableColors.length > 0 ? (
                                    <Grid item xs={12} md={6}>
                                        <FormControl fullWidth required>
                                            <InputLabel id="color-select-label">
                                                {translate('construction-props:COLOR')}
                                            </InputLabel>
                                            <Select
                                                labelId="color-select-label"
                                                label={translate('construction-props:COLOR')}
                                                value={color}
                                                onChange={(e) => setColor(e.target.value)}
                                            >
                                                {availableColors.map((color) => (
                                                    <MenuItem key={color} value={color}>
                                                        {color}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                ) : undefined}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2">{translate('milling-jobs:DEADLINE')}</Typography>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDateFns}
                                        adapterLocale={getDateFnsLocale()}
                                    >
                                        <div>
                                            <DatePicker
                                                format={getDatePickerFormat()}
                                                minDate={new Date()}
                                                value={deadline}
                                                onChange={handleDateChange}
                                            />
                                        </div>
                                    </LocalizationProvider>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        minRows={6}
                                        multiline
                                        value={comment}
                                        onChange={handleCommentChange}
                                        placeholder={translate('orders:COMMENT')}
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                            <Box mt={3}>
                                <FilesImagesUploadAreaAndTable
                                    files={files as any}
                                    onDeleteFile={() => console.log('TODO')}
                                    onFileChange={handleChange}
                                    showAsSimpleList={true}
                                    handleRemoveFile={removeFile}
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <OrderShadeInfoPanel restorations={restorations} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>{translate('common:CANCEL')}</Button>
                    <Button disabled={!isValid()} onClick={() => handleAddJobClick()} variant="contained">
                        {translate('milling-jobs:ADD_JOB')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export default AddMillingJobDialog;
